import { errorDetailsOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { useCallback, useMemo, useState } from "react";
import { type BillingError, billingProblemOf } from "../billing/problems";
import {
	type BillingSettingsDraft,
	checkSettings,
	type SettingsErrors,
	type SettingsField,
	sampleBill,
	settingsChanges,
	settingsFieldOf,
	settingsToDraft,
} from "../billing/settings-form";
import { useCurrentMembership } from "./use-current";
import { useCan } from "./use-navigation";
import { useRestaurantId } from "./use-order";
import { useStoredDraft } from "./use-stored-draft";

const baseOf = (draft: BillingSettingsDraft) => JSON.stringify(draft);

/**
 * More > Billing settings (MG-04, P4-T23): the form kept on the device
 * while it differs from what is saved (UX rule 4), field problems as the
 * server would find them (shown after the first Save), Save sending only
 * what changed, and `preview`: a sample bill drawn from the form as it is
 * now, through billing-core. `sampleNames` are the sample's three dish
 * names in the app's language. Saving needs `settings.manage`.
 */
export function useBillingSettingsForm(sampleNames: readonly [string, string, string]) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const restaurantId = useRestaurantId();
	const restaurant = useCurrentMembership().data?.restaurant ?? null;
	const canSave = useCan("settings.manage");
	const query = useQuery(trpc.staff.billing.getSettings.queryOptions());
	const saved = query.data ?? null;
	const initial = useMemo(() => (saved ? settingsToDraft(saved) : null), [saved]);
	const stored = useStoredDraft(`billingSettings.${restaurantId ?? "none"}`, initial, baseOf);
	const [submitted, setSubmitted] = useState(false);
	const [serverErrors, setServerErrors] = useState<SettingsErrors>({});
	const [problem, setProblem] = useState<BillingError | null>(null);
	const [savedAt, setSavedAt] = useState<number | null>(null);
	const mutation = useMutation(trpc.staff.billing.updateSettings.mutationOptions());

	const draft = stored.draft;
	const check = draft ? checkSettings(draft) : null;
	const errors: SettingsErrors = {
		...serverErrors,
		...(submitted && check && !check.ok ? check.errors : {}),
	};
	const [first, second, third] = sampleNames;
	const preview = useMemo(
		() =>
			draft && saved
				? sampleBill(draft, {
						names: [first, second, third],
						restaurantName: restaurant?.name ?? "",
						defaultTaxRateBp: saved.defaultTaxRateBp,
						timeZone: restaurant?.timezone,
					})
				: null,
		[draft, saved, first, second, third, restaurant?.name, restaurant?.timezone],
	);

	const { update } = stored;
	const set = useCallback(
		<K extends SettingsField>(field: K, value: BillingSettingsDraft[K]) => {
			setSavedAt(null);
			setProblem(null);
			setServerErrors((e) => {
				if (!(field in e)) return e;
				const next = { ...e };
				delete next[field];
				return next;
			});
			update((d) => ({ ...d, [field]: value }));
		},
		[update],
	);

	const save = useCallback(async (): Promise<boolean> => {
		if (!check || !saved || !canSave) return false;
		setSubmitted(true);
		setProblem(null);
		if (!check.ok) return false;
		const changes = settingsChanges(check.values, saved);
		if (Object.keys(changes).length === 0) {
			setSavedAt(Date.now());
			return true;
		}
		try {
			const next = await mutation.mutateAsync(changes);
			queryClient.setQueryData(trpc.staff.billing.getSettings.queryKey(), next);
			stored.forget();
			stored.reset(settingsToDraft(next));
			setSubmitted(false);
			setServerErrors({});
			setSavedAt(Date.now());
			return true;
		} catch (error) {
			const found = billingProblemOf(error);
			const field = settingsFieldOf(found.field);
			if (field)
				setServerErrors({
					[field]: errorDetailsOf(error).reason === "required" ? "required" : "invalid",
				});
			else setProblem(found);
			return false;
		}
	}, [check, saved, canSave, mutation, queryClient, trpc, stored]);

	return {
		status: draft ? ("ready" as const) : query.isError ? ("error" as const) : ("loading" as const),
		retry: () => void query.refetch(),
		saved,
		draft,
		set,
		errors,
		/** The form came back from the device: say so, with Discard. */
		restored: stored.restored,
		dirty: stored.dirty,
		discard: () => {
			stored.forget();
			stored.reset();
			setSubmitted(false);
			setServerErrors({});
		},
		canSave,
		save,
		saving: mutation.isPending,
		/** Just saved: say so until the next change. */
		justSaved: savedAt !== null,
		problem,
		preview,
	};
}
